import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  LinearProgress,
} from "@mui/material";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import { firestore as db, auth } from "../../firebase";
import { uploadFileWithProgress } from "../../utils/firebaseUtils";

export default function AdvancedChecklistForm({ onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const handleFileChange = (e) => {
    const chosen = Array.from(e.target.files || []);
    setFiles(chosen);
    setPreviews(
      chosen.map((f) => ({
        url: URL.createObjectURL(f),
        type: f.type,
        name: f.name,
      }))
    );
  };

  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [previews]);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!auth.currentUser) {
      alert("Please sign in to create a checklist.");
      return;
    }

    setIsSaving(true);
    try {
      // 1) create checklist doc first
      const docRef = await addDoc(collection(db, "amc_checklists"), {
        title,
        description,
        createdBy: auth.currentUser.email || auth.currentUser.uid,
        createdAt: new Date(),
        media: [],
      });

      const uploaded = [];
      // 2) upload files with progress
      for (const file of files) {
        const onProgress = (percent) => {
          setProgressMap((m) => ({ ...m, [file.name]: percent }));
        };
        const meta = await uploadFileWithProgress(
          file,
          `amc_checklists/${docRef.id}/files`,
          onProgress
        );
        uploaded.push(meta);
      }

      // 3) update checklist with media
      await updateDoc(doc(db, "amc_checklists", docRef.id), {
        media: uploaded,
      });

      setTitle("");
      setDescription("");
      setFiles([]);
      setPreviews([]);
      setProgressMap({});

      if (onSaved) onSaved();
      alert("Checklist created");
    } catch (err) {
      console.error(err);
      alert("Failed to save checklist: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Create Advanced AMC Checklist
      </Typography>

      <TextField
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        required
        sx={{ mb: 2 }}
      />

      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        rows={3}
        sx={{ mb: 2 }}
      />

      <div>
        <input
          type="file"
          accept="image/*,video/*,audio/*"
          multiple
          onChange={handleFileChange}
        />
      </div>

      <Box sx={{ mt: 2 }}>
        {previews.map((p) => (
          <Box key={p.name} sx={{ mb: 2 }}>
            {p.type.startsWith("image/") && (
              <img src={p.url} alt={p.name} style={{ maxWidth: 220 }} />
            )}
            {p.type.startsWith("audio/") && <audio controls src={p.url} />}
            {p.type.startsWith("video/") && (
              <video controls src={p.url} style={{ maxWidth: 320 }} />
            )}
            <div>{p.name}</div>
            {progressMap[p.name] != null && (
              <LinearProgress
                variant="determinate"
                value={progressMap[p.name]}
              />
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" type="submit" disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Checklist"}
        </Button>
      </Box>
    </Box>
  );
}
